// Get the latitude and longitude from a given address
// using one of the variety of api calls (Google Map, Geocodio & PositionStack).
//
// Example:
//  const geocodingService = new GeocodingService({
//   googleAPIKey: "AIzaSyD-q-_z2mJLF55B6zq31Rtmle2591_4Vwc",
//   geocodioAPIKey: "661363686f316d1dc936dc113d9d968dc381d6e",
//   positionStackAPIKey: "91aba6aa167ff5268baced028e60aa77",
//  });
//
// await geocodingService.getLagAndLong({ address: `Top Albion, Mandeville, Manchester`,});
export default class GeocodingService {

  //initialize object with appropriate key from values passed from caller
  constructor({ googleAPIKey, positionStackAPIKey, geocodioAPIKey }) {
    if (googleAPIKey) {
      this.service = 'google';
      this.apiKey = googleAPIKey;
      return;
    }

    if (positionStackAPIKey) {
      this.service = 'position-stack';
      this.apiKey = positionStackAPIKey;
      return;
    }

    if (geocodioAPIKey) {
      this.service = 'geocodio';
      this.apiKey = geocodioAPIKey;
      return;
    }

    this.service = 'default';
  }

  //return geocoding service
  getSelectedService() {
    return this.service;
  }

  //return the latitude and longitude from a given address
  //in the format {success: boolean, data: { lat: number, lng: number, address: string}, source: string}
  //otherwise returns a message indicating the issue.
  async getLagAndLong(address) {
    const apiFunc = this.selectGeoCodingAPI().bind(this);
    return apiFunc({ ...address });
  }

  //select geocoding api function
  selectGeoCodingAPI() {
    switch (this.service) {
      case 'google':
        return this.queryUsingGoogleAPI;
      case 'position-stack':
        return this.queryUsingPositionStackAPI;
      case 'geocodio':
        return this.queryUsingGeocodioAPI;
      default:
        return this.queryUsingDefaultAPI;
    }
  }

  async queryUsingGoogleAPI({ address, country }) {

    return new Promise(async (resolve) => {
      let res;
      let success = false;
      let data = {};

      try {
        let url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${this.apiKey}`;
        if (country)
          url += `&components=country:${encodeURIComponent(country)}`;

        res = await fetch(url);
      } catch (error) {
        console.log('error interacting with google geocoding api: ', error);
      }

      //check if there is errors sending the request
      //and inform caller
      if (!res || !res.ok) {
        data.message = "An unknown error has occurred. Please check your connection and try again.";
        return resolve({
          success,
          data,
          source: this.service,
        })
      }

      //parse response from api
      const { status, results } = await res.json();

      //handle successful and error responses
      switch (status) {
        case 'ZERO_RESULTS':
          data.message = "No results were found for the address given. Please try using a different address.";
          break;
        case 'OVER_DAILY_LIMIT':
          data.message = `Your daily limit was exceeded. Please see link for possible solutions: <a href="https://developers.google.com/maps/documentation/geocoding/requests-geocoding#StatusCodes">OVER_DAILY_LIMIT</a>`;
          break;
        case 'OVER_QUERY_LIMIT':
          data.message = "You are over your quota for the set period";
          break;
        case 'REQUEST_DENIED':
          data.message = `Your request was denied. Please see link for possible solutions: Please see link for possible solutions: <a href="https://developers.google.com/maps/documentation/geocoding/requests-geocoding#StatusCodes">REQUEST_DENIED</a>`;
          break;
        case 'UNKNOWN_ERROR':
          data.message = "An unknow error has occurred on google servers. This request may succeed if you try again.";
          break;
        case 'OK':
          if (results[0]) {
            success = true;
            data = {
              address: results[0].formatted_address,
              ...results[0].geometry.location,
            };
            break;
          }
          data.message = "No results were found for the address given. Please try using a different address.";
      }

      //send results back to caller
      resolve({
        success,
        source: this.service,
        data,
      })
    });
  }

  async queryUsingPositionStackAPI({ address, country }) {
    return new Promise(async (resolve) => {
      let res;
      let success = false;
      let data = {};

      try {
        let url = `http://api.positionstack.com/v1/forward?access_key=${this.apiKey}&query=${encodeURIComponent(address)}`;
        if (country)
          url += `&country=${encodeURIComponent(country)}`;

        res = await fetch(url);
      } catch (error) {
        console.log('error interacting with position stack geocoding api: ', error);
      }

      //check if there is errors sending the request
      //and inform caller
      if (!res || !res.ok) {
        res = res ? res : {status: 0};

        //check error messages and add appropriate responses
        switch (res.status) {
          case 401:
            data.message = "An invalid API was supplied or user account is inactive."
            break;
          case 429:
            data.message = "The given user account has reached its monthly allowed request volume or has reached the rate limit."
            break;
          case 404:
            data.message = "The given API endpoint does not exist or the resource was not found."
            break;
          case 500:
            data.message = "An unknown error has occurred on position stack servers. This request may succeed if you try again.";
            break;
          default:
            data.message = "An unknown error has occurred. Please check your connection and try again.";
        }

        return resolve({
          success,
          source: this.service,
          data,
        })
      }

      //parse response from api
      const result = await res.json();

      //check if results were returned in the response
      if (!result.data || result.data.length == 0) {
        data.message = "No results were found for the address given. Please try using a different address.";
      } else {
        success = true;
        data = {
          lat: result.data[0].latitude,
          lng: result.data[0].longitude,
          address: `${result.data[0].street}, ${result.data[0].country}`,
        };
      }

      //return successful or error results back to caller
      resolve({
        success,
        source: this.service,
        data
      })

    });
  }

  async queryUsingGeocodioAPI({ address, street, city, state }) {
    return new Promise(async (resolve) => {
      let res;
      let success = false;
      let data = {};

      try {
        let url = `https://api.geocod.io/v1.7/geocode?api_key=${this.apiKey}${this.buildSearchParams({ address, street, city, state })}`;
        res = await fetch(url);
      } catch (error) {
        console.log('error interacting with geocodio geocoding api: ', error);
      }

      //check if there is errors sending the request
      //and inform caller
      if (!res || !res.ok) {
        res = res ? res : {status: 0};

        switch (res.status) {
          case 422:
            data.message = "No results were found for the address given. Please try using a different address.";
            break;
          case 403:
            data.message = "Invalid API key, or other reason why access is forbidden."
            break;
          case 429:
            data.message = "The given user account has reached the rate limit. Contact your administrator to resolve."
            break;
          case 500:
            data.message = "An unknown error has occurred on geocodio servers. This request may succeed if you try again.";
            break;
          default:
            data.message = "An unknown error has occurred. Please check your connection and try again.";
        }

        return resolve({
          success,
          data,
          source: this.service,
        })
      }

      //parse response from api
      const { results } = await res.json();

      //check if results were returned in the response
      if (!results || results.length == 0) {
        data.message = "No results were found for the address given. Please try using a different address.";
      } else {
        success = true;
        data = {
          ...results[0].location,
          address: results[0].formatted_address
        };
      }

      //return successful or error results back to caller
      resolve({
        success,
        data,
        source: this.service,
      })

    });
  }

  async queryUsingDefaultAPI({ address, street, city, state, country }) {
    return new Promise(async (resolve) => {
      let res;
      let success = false;
      let data = {};

      try {
        let url = `https://geocode.maps.co/search?api_key=none${this.buildSearchParams({ address, street, city, state, country })}`
        res = await fetch(url);
      } catch (error) {
        console.log('error interacting with default geocoding api: ', error);
      }

      //check if there is errors sending the request
      //and inform caller
      if (!res || !res.ok) {
        res = res ? res : {status: 0};

        switch (res.status) {
          case 429:
            data.message = "Rate limit reached. Please wait a few seconds and try again."
            break;
          case 500:
            data.message = "An unknown error has occurred on geocodio servers. This request may succeed if you try again.";
            break;
          default:
            data.message = "An unknown error has occurred. Please check your connection and try again.";
        }

        return resolve({
          success,
          data,
          source: this.service,
        })
      }

      //parse response from api
      const results = await res.json();

      //check if results were returned in the response
      if (!results || results.length == 0) {
        data.message = "No results were found for the address given. Please try using a different address.";
      } else {
        success = true;
        data = {
          lat: results[0].lat,
          lng: results[0].lon,
          address: results[0].display_name
        };
      }

      //return successful or error results back to caller
      resolve({
        success,
        data,
        source: this.service,
      })

    });
  }

  buildSearchParams({ address, street, city, state, country }) {
    let url = ``;

    if (address)
      return `&q=${encodeURIComponent(address)}`;

    if (street)
      url += `&street=${encodeURIComponent(street)}`;

    if (city)
      url += `&city=${encodeURIComponent(city)}`;

    if (state)
      url += `&state=${encodeURIComponent(state)}`;

    if (country)
      url += `&country=${encodeURIComponent(country)}`;

    return url;
  }
}

